import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAppActions } from '../AppProvider';
import { useHistory } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tabs from '@afosto/components-react/Tabs';
import Popper from '@afosto/components-react/Popper';
import Box from '@material-ui/core/Box';
import Fab from '@material-ui/core/Fab';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import styled from 'styled-components';
import Title from '@afosto/components-react/Title';
import Icon from '../Icon';
import clsx from 'clsx';

const PrimaryActionButton = styled(Fab)`
  && {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 1;
  }
`;

const ExpandedTitle = styled(Title)`
  flex-grow: 1;
  padding-left: ${props => props.theme.spacing(1)}px;
`;

const StyledIcon = styled(Icon)`
  && {
    &.is-extended {
      margin-right: ${props => props.theme.spacing(1)}px;
    }
  }
`;

const StyledSpeedDial = styled(SpeedDial)`
  && {
    position: fixed;
    bottom: ${props => props.theme.spacing(2)}px;
    right: ${props => props.theme.spacing(2)}px;

    .MuiSpeedDialAction-staticTooltipLabel {
      white-space: nowrap;
    }
  }
`;

const MobilePageHeader = ({
  title,
  titleHidden,
  startAdornment,
  breadcrumbs,
  secondaryActions,
  subNavigation,
  useBreadcrumbNavigation,
  linkComponent,
  showMenu,
  primaryAction,
  forceMenu,
}) => {
  const history = useHistory();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [actions] = useAppActions();

  const menuMarkup = showMenu && (
    <IconButton
      edge="start"
      color="inherit"
      aria-label="open drawer"
      onClick={() => actions.setIsDrawerOpen(true)}
    >
      <Icon icon={['fal', 'bars']} size="small" />
    </IconButton>
  );

  let backButtonProps = breadcrumbs?.length > 0 && {
    component: linkComponent,
    to: breadcrumbs.slice(-2).reverse().pop().path,
  };

  if (breadcrumbs?.length > 0 && !useBreadcrumbNavigation) {
    backButtonProps = {
      onClick: () => history.goBack(),
    };
  }

  const backMarkup = breadcrumbs?.length > 0 && (
    <IconButton edge="start" {...backButtonProps}>
      <Icon icon={['fal', 'arrow-left']} size="small" />
    </IconButton>
  );

  const getPrimaryActionMarkup = () => {
    if (!primaryAction) {
      return undefined;
    }

    const hasMultipleActions = primaryAction.actions && primaryAction.actions.length > 0;

    return hasMultipleActions ? (
      <StyledSpeedDial
        ariaLabel={primaryAction.text}
        open={speedDialOpen}
        onOpen={() => setSpeedDialOpen(true)}
        onClose={() => setSpeedDialOpen(false)}
        icon={<Icon icon={primaryAction.icon || ['fal', 'plus']} />}
      >
        {primaryAction.actions.map(action => (
          <SpeedDialAction
            key={action.text}
            tooltipTitle={action.text}
            tooltipOpen
            icon={<Icon icon={action.icon} />}
            FabProps={{
              disabled: action.disabled,
            }}
            onClick={action.action}
          />
        ))}
      </StyledSpeedDial>
    ) : (
      <PrimaryActionButton
        color="primary"
        variant={primaryAction.FABVariant || 'round'}
        onClick={primaryAction.action}
        disabled={primaryAction.disabled}
      >
        <StyledIcon
          icon={primaryAction.icon || ['fal', 'plus']}
          size="small"
          className={clsx({ 'is-extended': primaryAction.FABVariant === 'extended' })}
        />
        {primaryAction.FABVariant && primaryAction.FABVariant === 'extended' && primaryAction.text}
      </PrimaryActionButton>
    );
  };

  const primaryActionMarkup = getPrimaryActionMarkup();

  const subNavigationMarkup = subNavigation && (
    <Box pr={1} pl={1} pb={1}>
      <Tabs
        variant="scrollable"
        scrollButtons="off"
        current={subNavigation.current}
        tabs={subNavigation.tabs}
        onChange={subNavigation.onChange}
      />
    </Box>
  );

  const [secondaryActionsIsOpen, setSecondaryActionsIsOpen] = useState(false);
  const secondaryActionsButtonRef = useRef(null);

  const handleToggle = () => setSecondaryActionsIsOpen(!secondaryActionsIsOpen);
  const handleCloseSecondaryActions = e => {
    if (secondaryActionsButtonRef.current.contains(e.target)) {
      return;
    }
    setSecondaryActionsIsOpen(false);
  };

  const shortCutActions =
    (secondaryActions && secondaryActions.filter(action => action.shortCut === true)) || [];
  const dropdownActions = secondaryActions || [];

  const shortCutActionsMarkup = shortCutActions && shortCutActions.length > 0 && (
    <>
      {shortCutActions.map(({ text, action, icon }, idx) => (
        <IconButton
          onClick={action}
          aria-label={text}
          key={text}
          edge={idx === shortCutActions.length - 1 && dropdownActions.length === 0 ? 'end' : null}
        >
          <Icon icon={icon} size="small" />
        </IconButton>
      ))}
    </>
  );

  const dropdownActionsMarkup = dropdownActions && dropdownActions.length > 0 && (
    <>
      <IconButton
        edge="end"
        color="inherit"
        onClick={handleToggle}
        buttonRef={secondaryActionsButtonRef}
      >
        <Icon size="small" icon={['fal', 'ellipsis-v']} />
      </IconButton>
      <Popper
        handleClose={handleCloseSecondaryActions}
        anchorEl={secondaryActionsButtonRef}
        open={secondaryActionsIsOpen}
        position="bottom-end"
      >
        <ClickAwayListener onClickAway={handleCloseSecondaryActions}>
          <MenuList>
            {dropdownActions.map(({ text, action, disabled, icon, isLoading, color }) => {
              const onClick = e => {
                handleCloseSecondaryActions(e);
                action();
              };

              return (
                <MenuItem dense key={text} onClick={onClick} disabled={disabled}>
                  {icon && (
                    <ListItemIcon>
                      <Icon icon={icon} size="small" color={color} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{ color: color || 'textPrimary' }}
                  />
                  {isLoading && (
                    <ListItemSecondaryAction>
                      <CircularProgress size={15} />
                    </ListItemSecondaryAction>
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </ClickAwayListener>
      </Popper>
    </>
  );

  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={1}>
        <Toolbar>
          {startAdornment || (!forceMenu && backMarkup) || menuMarkup}
          <ExpandedTitle
            title={title}
            variant="h6"
            noWrap
            titleHidden={titleHidden}
            disableMinHeight
          />
          {shortCutActionsMarkup}
          {dropdownActionsMarkup}
        </Toolbar>
        {subNavigation && subNavigationMarkup}
      </AppBar>
      {primaryAction && primaryActionMarkup}
    </>
  );
};

const breadcrumbShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
  active: PropTypes.bool,
});

const secondaryActionShape = PropTypes.shape({
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.array,
  isLoading: PropTypes.bool,
});

const subNavigationShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
});

const defaultPrimaryActionShape = {
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.array,
  FABVariant: PropTypes.string,
};

MobilePageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  titleHidden: PropTypes.bool,
  forceMenu: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(breadcrumbShape),
  linkComponent: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  startAdornment: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  primaryAction: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func,
    disabled: PropTypes.bool,
    icon: PropTypes.array,
    FABVariant: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.shape(defaultPrimaryActionShape)),
  }),
  secondaryActions: PropTypes.arrayOf(secondaryActionShape),
  subNavigation: PropTypes.shape({
    current: PropTypes.string,
    tabs: PropTypes.arrayOf(subNavigationShape),
    onChange: PropTypes.func,
  }),
  showMenu: PropTypes.bool,
  useBreadcrumbNavigation: PropTypes.bool,
};

MobilePageHeader.defaultProps = {
  titleHidden: false,
  forceMenu: false,
  breadcrumbs: undefined,
  startAdornment: undefined,
  primaryAction: undefined,
  secondaryActions: undefined,
  subNavigation: undefined,
  linkComponent: 'a',
  showMenu: true,
  useBreadcrumbNavigation: true,
};

export default MobilePageHeader;
